import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

// Store
import { env } from '@amfam/environment';
import { PolicySelectors, PolicySummarySelectors } from '@amfam/policy/data-access';
import { EdeliverySelectors } from '@amfam/policy/e-delivery/data-access';
import { GoPaperLessSelectors } from '@amfam/policy/go-paperless/data-access';
import { GenericProductType, Policy, VehicleTypeConstants } from '@amfam/policy/models';
import { AnalyticsFacade, PageAnalyticParams } from '@amfam/shared/analytics';
import { userQuery, UserState } from '@amfam/shared/user';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { impersonationQuery } from '@amfam/shared/utility/impersonation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  DsModalService,
  LoadingSpinnerService,
  PageTitleService
} from '@amfam/ui-kit';

import * as fromRoot from '../../core/store';

@Component({
  selector: 'app-policy-list',
  templateUrl: './policy-list.component.html',
  styleUrls: ['./policy-list.component.scss'],
  standalone: false
})
export class PolicyListComponent implements OnInit, OnDestroy {
  private currentUser: UserState;
  vehiclePolicies: Policy[] = new Array<Policy>();
  autoPolicies: Policy[] = new Array<Policy>();
  cyclePolicies: Policy[] = new Array<Policy>();
  rvPolicies: Policy[] = new Array<Policy>();
  watercraftPolicies: Policy[] = new Array<Policy>();
  propertyPolicies: Policy[] = new Array<Policy>();
  umbrellaPolicies: Policy[] = new Array<Policy>();
  lifePolicies: Policy[] = new Array<Policy>();
  hasAdvancePolicies = false;
  hasPolicies = false;
  isEnrolled = false;
  isEligibleForPaperless = false;
  policyCount: number;

  private stop$: Subject<void> = new Subject<void>();
  public dataLoading: Observable<Boolean>;
  public advancedAutoPolicies$ = this.store.pipe(
    select(
      fromRoot.getCoverageChangeRisks,
      this.feature.isEnabled('kyd_model_coverage') ? [] : env.modelCoverageDisabledStates
    )
  );
  public hasCompAndCollisonCoverage$ = this.store.select(
    fromRoot.hasAnyPolicyCompAndCollisonCoverageAndCarLightTruck
  );

  public isImpersonating$ = this.store.select(impersonationQuery.isImpersonating);
  policySummaries$ = this.store
    .select(PolicySummarySelectors.selectNonOperatorPolicySummaries)
    .pipe(map(summaries => summaries.filter(summary => summary.sourcePath !== 'HOMESITE')));

  componentData: any;
  // **** Start of Analytics data for this component
  private pageAnalytic: PageAnalyticParams = {
    pageName: 'MyAccount:Policies',
    experience: '',
    primaryCategory: 'My Account',
    subCategory1: 'Policies',
    subCategory2: '',
    subCategory3: ''
  };
  // **** End of Analytics data for this component

  vehicleTypeConstants = VehicleTypeConstants;
  genericProductType = GenericProductType;
  policyUnavailableModalId = 'noDetailModal';
  summariesError$ = this.store.select(PolicySummarySelectors.selectPolicySummariesHasError);
  summariesLoaded$ = this.store.select(PolicySummarySelectors.selectLoaded);
  isAutoPolicyCancelled = false;
  isUmbrellaPolicyCancelled = false;
  isPropertyPolicyCancelled = false;

  constructor(
    private copyService: CopyService,
    private pageTitle: PageTitleService,
    public spinner: LoadingSpinnerService,
    private analyticsFacade: AnalyticsFacade,
    private store: Store<fromRoot.RootState>,
    private dockingService: DockingBarService,
    private feature: FeatureFlagService,
    private modalService: DsModalService
  ) {
    this.componentData = this.copyService.getComponentData('policy');
    this.spinner.start();
  }

  ngOnInit() {
    this.pageTitle.set('Policies');
    this.dockingService.registerHeading('Policies Overview');
    this.dataLoading = combineLatest(
      this.store.select(PolicySelectors.selectPoliciesLoading),
      this.store.select(EdeliverySelectors.getEdeliveryTermsAndConditionsLoading),
      (detailLoading, eDeliveryTermsAndConditionsLoading) =>
        detailLoading || eDeliveryTermsAndConditionsLoading
    );

    combineLatest(
      this.store.select(PolicySelectors.selectPropertyPolicies),
      this.store.select(PolicySelectors.selectVehiclePolicies),
      this.store.select(PolicySelectors.selectAutoPolicies),
      this.store.select(PolicySelectors.selectCyclePolicies),
      this.store.select(PolicySelectors.selectRvPolicies),
      this.store.select(PolicySelectors.selectWatercraftPolicies),
      this.store.select(PolicySelectors.selectUmbrellaPolicies),
      this.store.select(PolicySelectors.selectLifePolicies),
      this.store.select(PolicySelectors.selectAdvancePolicies),
      this.store.select(PolicySelectors.selectPolicies),
      this.store.select(PolicySelectors.selectNumberOfPolicies),
      this.store.select(userQuery.selectUserState),
      this.store.select(GoPaperLessSelectors.selectIsEnrolledGoPaperless),
      this.dataLoading,
      this.store.select(EdeliverySelectors.getEdeliveryTermsAndConditionsHasError),
      this.store.select(PolicySelectors.selectIsEligibleForPaperless),
      this.policySummaries$,
      (
        homePolicies,
        vehiclePolicies,
        autoPolicies,
        cyclePolicies,
        rvPolicies,
        watercraftPolicies,
        umbrellaPolicies,
        lifePolicies,
        hasAdvancePolicies,
        hasPolicies,
        policyCount,
        userState,
        isEnrolled,
        loading,
        eDeliveryTermsAndConditionsError,
        eligibleForPaperless,
        policySummaries
      ) => {
        return {
          homePolicies: homePolicies,
          vehiclePolicies: vehiclePolicies,
          autoPolicies: autoPolicies,
          cyclePolicies: cyclePolicies,
          rvPolicies: rvPolicies,
          watercraftPolicies: watercraftPolicies,
          umbrellaPolicies: umbrellaPolicies,
          lifePolicies: lifePolicies,
          hasAdvancePolicies: hasAdvancePolicies,
          hasPolicies: hasPolicies,
          policyCount: policyCount,
          userState: userState,
          isEnrolled: isEnrolled,
          loading: loading,
          eDeliveryTermsAndConditionsError: eDeliveryTermsAndConditionsError,
          isEligibleForPaperless: eligibleForPaperless,
          policySummaries
        };
      }
    )
      .pipe(
        takeUntil(this.stop$),
        filter(state => !state.loading)
      )
      .subscribe(
        state => {
          this.propertyPolicies = state.homePolicies;
          this.vehiclePolicies = state.vehiclePolicies;
          this.autoPolicies = state.autoPolicies;
          this.cyclePolicies = state.cyclePolicies;
          this.rvPolicies = state.rvPolicies;
          this.watercraftPolicies = state.watercraftPolicies;
          this.umbrellaPolicies = state.umbrellaPolicies;
          this.lifePolicies = state.lifePolicies;
          this.hasAdvancePolicies = !!_get(state, 'hasAdvancePolicies.length', 0);
          this.hasPolicies = !!_get(state, 'hasPolicies.length', 0);
          this.policyCount = state.policyCount;
          this.currentUser = state.userState;
          this.isEnrolled = state.isEnrolled && !state.eDeliveryTermsAndConditionsError;
          this.isEligibleForPaperless = state.isEligibleForPaperless;
          this.checkPolicyStatus();
          this.spinner.stop();
        },
        error => {
          this.analyticsFacade.trackPageAndError(this.pageAnalytic, 'PolicyLoadError');
          throw error;
        }
      );

    this.analyticsFacade.trackPage(this.pageAnalytic);
  }

  openPolicyUnavailableModal() {
    this.modalService.open(this.policyUnavailableModalId);
  }

  closePolicyUnavailableModal() {
    this.modalService.close(this.policyUnavailableModalId);
  }

  checkPolicyStatus() {
    if (this.autoPolicies[0] && this.autoPolicies[0].isPolicyCancelled) {
      this.isAutoPolicyCancelled = true;
    } else if (this.umbrellaPolicies[0] && this.umbrellaPolicies[0].isPolicyCancelled) {
      this.isUmbrellaPolicyCancelled = true;
    } else if (this.propertyPolicies[0] && this.propertyPolicies[0].isPolicyCancelled) {
      this.isPropertyPolicyCancelled = true;
    }
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
